import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { heroBanners } from '@/mocks/hero';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const count = heroBanners.length;

  const go = useCallback(
    (next: number) => setIndex((next + count) % count),
    [count]
  );

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
  };

  return (
    <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-primary-950" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {heroBanners.map((banner, i) => (
        <div
          key={banner.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        >
          <img
            src={banner.image}
            alt={banner.title}
            className={`w-full h-full object-cover object-top ${i === index ? 'animate-kenburns' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="absolute inset-0 z-20 flex items-center">
        <div className="w-full max-w-8xl mx-auto px-4 md:px-8">
          {heroBanners.map((banner, i) => (
            <div
              key={banner.id}
              className={`max-w-2xl ${i === index ? 'block animate-fade-up' : 'hidden'}`}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold font-label tracking-wide">
                <i className="ri-fire-fill"></i>
                {banner.kicker}
              </span>
              <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-background-50 drop-shadow">
                {banner.title}
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1 text-accent-400 font-bold">
                  <i className="ri-star-fill"></i>
                  {banner.rating.toFixed(1)}
                </span>
                <span className="whitespace-nowrap">{banner.meta}</span>
              </div>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200 max-w-xl line-clamp-3">
                {banner.description}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${banner.movieId}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  观看详情
                </Link>
                <Link
                  to={`/detail/${banner.movieId}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 border border-background-50/40 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap backdrop-blur"
                >
                  <i className="ri-information-line text-lg"></i>
                  作品信息
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 border border-background-50/30 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 border border-background-50/30 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {heroBanners.map((banner, i) => (
          <button
            key={banner.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}